/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./LaunchIntoExploreHandler',
	'../../../../app/nls/StringResources',
	'../../../../app/util/ErrorUtils',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(LaunchIntoExploreHandler, StringResources, ErrorUtils,
	ApiFactory, ContentActionsProviderApi) {

	const DATAPLAYER_VIS_NAME = 'dataPlayer';
	const LAUNCH_MODES = ['new', 'existing'];

	class LaunchIntoExploreAction {
		constructor({ features }) {
			this._dashboard = features.API;
			this._contentActions = features.ContentActions;

			this._controller = null;
			this._launchHandler = null;

			this._icons = features.Icons;
		}

		initialize() {
			this._contentActions.registerProvider('LaunchIntoExplore', this.getAPI());
		}

		getAPI() {
			if (!this._api) {
				this._api = ApiFactory.createAPI(this, [ContentActionsProviderApi]);
			}
			return this._api;
		}

		getLifeCycleHandlers() {
			return [{
				name: 'post:dashboard.initialize',
				action: this.postDashboardInitialize.bind(this)
			}];
		}

		postDashboardInitialize() {
			this._controller = this._dashboard.getFeature('InteractionController.internal');
			this._launchHandler = new LaunchIntoExploreHandler({
				dashboard: this._dashboard,
				controller: this._controller
			});

			return Promise.resolve();
		}

		destroy() {}

		getContentActionList(idList) {
			if(!ErrorUtils.hasCapability(this._dashboard, 'canUseExploration')
				|| idList.length === 0
				|| !this._validateWidgetTypes()
				|| this._dashboard.getMode() === this._dashboard.MODES.EDIT_GROUP) {
				return [];
			}

			return [
				{
					name: 'launchExplore',
					label: StringResources.get('exploreData'),
					icon: this._icons.getIcon('explore').id,
					type: 'NextView',
					order: 1,
					viewModule: 'lib/@waca/dashboard-common/dist/ui/dialogs/CommonSelectItemsDialog',
					viewOptions: {
						height: 202,
						width: 402,
						possibleItems: [{
							id: LAUNCH_MODES[0],
							localizedString: StringResources.get('launchNewExplore')
						}, {
							id: LAUNCH_MODES[1],
							localizedString: StringResources.get('addToExisting')
						}],
						eventHandler: this,
						labelsView: StringResources.get('exploreData')
					}
				}
			];
		}

		/**
		 * This is the handler for CommonSelectItemsDialog that is called
		 * when the user selectes which exploration to launch into (new/existing)
		 *	@param {string} launchMode A value representing the item the user selected (see the spec returned from getCOntentActionList above)
		 */
		handleSelection(launchMode) {
			if (launchMode === LAUNCH_MODES[1]) {
				this._selectExistingExplore();
			} else {
				this._launchNewExplore();
			}
		}

		_isCustomVisType(definition, visId) {
			const CUSTOM_VIS_TYPES = ['visualizationPreview', 'com.ibm.vis.schematicsPreview'];
			const isSchematic = definition.getProperty('isSchematic');
			const isCustomViz = definition.getProperty('isCustomVis');
			return isSchematic || isCustomViz
				|| CUSTOM_VIS_TYPES.indexOf(visId) >= 0;
		}

		_isInvalidWidget(contentVisInfo) {
			if (!contentVisInfo) {
				return true;
			}

			const visId = contentVisInfo.getDefinition().getId();
			return visId === DATAPLAYER_VIS_NAME
				|| this._isCustomVisType(contentVisInfo.getDefinition(), visId);
		}

		_validateWidgetTypes() {
			const canvas = this._dashboard.getCanvas();

			const selectedItems = canvas ? canvas.getSelectedContentList({ type: 'widget.live' }) : [];
			const hasInvalidTypes = selectedItems.some(content => {
				const contentVisInfo = content.getFeature('Visualization');
				return this._isInvalidWidget(contentVisInfo);
			});

			return !hasInvalidTypes && selectedItems.length !== 0;
		}

		_filterResults(response) {
			// Filter to select only subfolders that user has 'read' + 'traverse' permissions
			// and explore that user has 'read' permission
			if (response.data) {
				response.data = response.data.filter(result => this._hasRequiredPermissions(result));
			} else {
				response.data = [];
			}
		}

		_hasRequiredPermissions(result) {
			const permissions = result.permissions;
			if (result.type === 'folder') {
				return permissions &&
					permissions.indexOf('read') > -1 &&
					permissions.indexOf('traverse') > -1;
			} else if (result.type === 'exploration') {
				return permissions && permissions.indexOf('read') > -1;
			}
			return false;
		}

		_selectExistingExplore() {
			this._dashboard.getGlassSvc('.ContentDialogFactory').then(dialogFactory => {
				const options = {
					'dataManipulationCallback': this._filterResults.bind(this),
					'dialogTitle': StringResources.get('addToExisting'),
					'extraUrlParameters': ['tags', 'permissions'],
					'multiSelect': false,
					'onOpenCallback': this._launchExistingExplore.bind(this),
					'primaryBtnText': StringResources.get('addTo'),
					'typesToOpen': ['explore'] // content_nav does not work with 'exploration'
				};

				// I don't like this. ContentDialogFactory#createOpenDialog needs glassContext
				this._dashboard.prepareGlassOptions(options);

				const openDialog = dialogFactory.createOpenDialog(options);
				openDialog.open();
			});
		}

		_launchExistingExplore(selectedItems) {
			// We can assume only ever one selected item because in the spec from
			// the method _selectExistingExplore we set "multiSelect" to false
			const { id: objectId } = selectedItems[0];
			this._launchHandler.launchExplore({
				content: {
					boardId: objectId,
					isAuthoringMode: true,
					objRef: objectId
				}
			});
		}

		_launchNewExplore() {
			this._launchHandler.launchExplore();
		}
	}

	return LaunchIntoExploreAction;
});
